'use client'

import useIsMounted from '@hooks/useIsMounted'
import useMobileDisabled from '@hooks/useMobileDisabled'
import Box from '@mui/joy/Box'
import { useIsCommentDrawControl } from '@store/isCommentDraw'
import Script from 'next/script'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useRecoilValue, useRecoilCallback } from 'recoil'
import { isMobile } from 'react-device-detect'
import { useIsAppControl, appInterface } from '@store/isApp'
import { DATE_FORMAT, dayjs } from '@utils/date'
import { useVideoResolution } from '@store/videoResolution'
import VideoContainer from './VideoContainer'
import { usePathname } from 'next/navigation'
import { useVideoMetaFixed } from '@hooks/useVideoMetaFixed'
import { useTTSControl, TTSState } from '@store/tts'
import { useNotification } from '@lib/notification'
import IconArticleVideoPlay from 'src/assets/icons/IconArticleVideoPlay'
import { createRoot } from 'react-dom/client'

interface SendAppPlayerPauseProps {
	isPause: boolean
}

interface VideoPlayerProps extends Omit<JTBCVideoPlayerProps, 'container'> {
	videoIdx: string
	/** 동영상 비율 @default '16/9' */
	ratio?: string
	isPIP?: boolean
	/** 비디오 재생 종료 시, 호출 함수 */
	onFinished?: () => void
	setVideoElement?: (value: HTMLVideoElement) => void
	setVideoCanPlay?: (value: boolean) => void
	setController?: (value: JTBCVideoPlayerWrapper | undefined) => void
}
export const VideoPlayer = ({
	videoIdx,
	src,
	themeColor,
	type,
	isPlayAd,
	isAutoPlay,
	isPIP = false,
	vodRedirectURL,
	ratio = '16/9',
	onFinished,
	setVideoElement,
	setVideoCanPlay,
	setController,
	isMuted,
}: VideoPlayerProps) => {
	const nativeEvent = useRecoilValue(appInterface)
	const { isApp, appInfo } = useIsAppControl()
	const mobileDisabled = useMobileDisabled()
	const { isCommentDraw } = useIsCommentDrawControl()
	const [vodPlayer, setVodPlayer] = useState<JTBCVideoPlayerWrapper>()
	const [vodElement, setVodElement] = useState<HTMLVideoElement>()
	const [vodCanPlay, setVodCanPlay] = useState<boolean>(false)
	const [vodMuted, setVodMuted] = useState<boolean>(true)
	const [enablePIP, setEnablePIP] = useState<boolean>(false)
	const { videoResolution, setVideoResolution } = useVideoResolution()
	const { notify } = useNotification()
	const { isTTSPlaying, setIsTTSPlaying } = useTTSControl()
	const [videoCurrentTime, setVideoCurrentTime] = useState<number | undefined>(() => {
		const savedTimeData = localStorage.getItem(`video-${videoIdx}-currentTime`)
		if (savedTimeData) {
			try {
				const { time, expiry } = JSON.parse(savedTimeData)
				if (expiry > Date.now()) {
					return time
				}
				localStorage.removeItem(`video-${videoIdx}-currentTime`)
			} catch (e) {
				localStorage.removeItem(`video-${videoIdx}-currentTime`)
			}
		}
		return undefined
	})

	const pathname = usePathname()
	const [isVodPlaying, setVodPlaying] = useState<boolean>(false)
	const [initAutoPlay, setInitAutoPlay] = useState(false)
	const [isLoadedVideo, setIsLoadedVideo] = useState(false)
	const isMounted = useIsMounted()
	const vodContainerRef = useRef<HTMLDivElement>(null)
	const scriptId = 'jtbc-video-player-script'
	const today = dayjs().format(DATE_FORMAT.DATE_AS_NUMBER)
	const previousVideoSrc = useRef<string | null>(null)
	const isLivePage = pathname.startsWith('/live')
	const isArticlePage = pathname.startsWith('/article')

	const onBeforeFullscreen = useCallback(() => {
		if (appInfo?.DType === 'A') {
			const html = document.querySelector('html')
			if (html) {
				html.style.overflow = 'hidden'
			}
		}
	}, [appInfo])
	const handleAppWithTTSButtonClose = useRecoilCallback(
		({ snapshot }) =>
			async () => {
				const ttsOverlay = document.querySelector('.tts-video-overlay')
				if (ttsOverlay) {
					ttsOverlay.remove()
				}

				const currentTTSState = await snapshot.getPromise(TTSState)

				if (currentTTSState.isTTSPlaying) {
					notify('본문 듣기를 종료 하였습니다.')
				}

				setIsTTSPlaying(false)
			},
		[setIsTTSPlaying, notify],
	)
	const handleChangeFullscreen = useCallback(
		(e: Event) => {
			handleAppWithTTSButtonClose()
			const { isFullscreen } = (e as CustomEvent).detail

			if (!isFullscreen) {
				if (appInfo?.DType === 'A') {
					const html = document.querySelector('html')
					if (html) {
						html.style.overflow = ''
					}
				}
			}
		},
		[appInfo, handleAppWithTTSButtonClose],
	)
	/** APP - Player 정지 여부 수신  */
	const sendAppPlayerPause = ((event: CustomEvent<SendAppPlayerPauseProps>) => {
		const isPaused = vodPlayer?.container
			.querySelector('.jtbcplayer-container')
			?.classList.value.split(' ')
			.includes('paused')

		if (!isPaused && event.detail.isPause) {
			vodPlayer?.pause()
		} else if (isPaused && !event.detail.isPause) {
			vodPlayer?.play()
		}
	}) as EventListener
	const handleChangeResolution = useCallback(
		(e: Event) => {
			const { resolution } = (e as CustomEvent).detail
			handleAppWithTTSButtonClose()

			if (isLivePage) {
				setVideoResolution(resolution)
			}
			setVodPlaying(true)
		},
		[isLivePage, setVideoResolution, handleAppWithTTSButtonClose],
	)
	const handleLoad = useCallback(() => {
		if (typeof JTBCVideoPlayer !== 'undefined' && vodContainerRef.current && !vodPlayer) {
			if (videoIdx !== previousVideoSrc.current) {
				previousVideoSrc.current = videoIdx || null
				// eslint-disable-next-line new-cap
				const vodVideoPlayer = new JTBCVideoPlayer.default({
					src,
					themeColor: {
						primary: '#88FFFC' as JTBCVideoPlayerColorHEX,
						secondary: '#88FFFC' as JTBCVideoPlayerColorHEX,
					},
					type,
					isPlayAd: isApp ? false : isPlayAd,
					isAutoFixTop: false,
					isAutoMiniPlayer: false,
					isAutoPlay,
					isMuted,
					vodRedirectURL,
					timeOffset: pathname.includes('video') ? videoCurrentTime : 0,
					resolution: videoResolution,
					container: vodContainerRef.current as HTMLDivElement,
					onBeforeFullscreen,
				})
				vodVideoPlayer.render()
				setVodPlayer(vodVideoPlayer)
				setController?.(vodVideoPlayer)
				setVodMuted(true)
			}
		}
	}, [
		vodPlayer,
		src,
		type,
		isPlayAd,
		vodRedirectURL,
		videoIdx,
		isApp,
		videoResolution,
		onBeforeFullscreen,
		pathname,
		isMuted,
		isAutoPlay,
		setController,
		videoCurrentTime,
	])

	const handleFinished = () => {
		onFinished?.()
		if (pathname.includes('video')) {
			setVideoCurrentTime(0)
			localStorage.removeItem(`video-${videoIdx}-currentTime`)
		}
	}

	const handlePIP = useCallback(() => {
		if (isPIP) {
			const vodPosition = vodPlayer?.container?.getBoundingClientRect()
			const vodClientHeight = vodPlayer?.container?.clientHeight ?? 0
			const isPIPMode = !!vodPlayer?.container?.querySelector('.pip-mode')
			if (vodClientHeight + (vodPosition?.top ?? 0) < 0) {
				if (!enablePIP && !isPIPMode) {
					vodPlayer?.onTogglePIP()
					setEnablePIP(true)
				}
			} else if (enablePIP) {
				if (isPIPMode) {
					vodPlayer?.onTogglePIP()
				}
				setEnablePIP(false)
			}
		}
	}, [vodPlayer, enablePIP, isPIP])

	useEffect(() => {
		if (isLoadedVideo) {
			handleLoad()
		}
	}, [isLoadedVideo, handleLoad])

	const handleDispatch = () => {
		window.dispatchEvent(new CustomEvent('loadedPlayer'))
		setIsLoadedVideo(true)
	}
	const handleLoadedVideo = (e: Event) => {
		const element = e.target as HTMLElement
		const videoEl = element.querySelector('video') as HTMLVideoElement

		const handleCanPlay = () => {
			setVodCanPlay(true)
			setVideoCanPlay?.(true)

			videoEl.removeEventListener('canplay', handleCanPlay)
		}
		videoEl.addEventListener('canplay', handleCanPlay)
		setVodElement(videoEl)
		setVideoElement?.(videoEl)
	}
	const handleVisibilityChange = useCallback(() => {
		if (document.hidden) {
			const isPaused = vodPlayer?.container
				.querySelector('.jtbcplayer-container')
				?.classList.value.split(' ')
				.includes('paused')
			if (!isPaused && !(!isMobile && !vodMuted)) {
				vodPlayer?.pause()
			}
		}
	}, [vodPlayer, vodMuted])

	const handleTimeUpdate = useCallback(
		(e: Event) => {
			const { currentTime } = (e as CustomEvent).detail
			const timeToSave = currentTime > 60 ? currentTime : 0

			if (pathname.includes('video')) {
				setVideoCurrentTime(timeToSave)
				if (currentTime > 60) {
					localStorage.setItem(
						`video-${videoIdx}-currentTime`,
						JSON.stringify({ time: timeToSave }),
					)
				} else {
					localStorage.removeItem(`video-${videoIdx}-currentTime`)
				}
			}
		},
		[videoIdx, pathname],
	)

	const handleChangeVideoMute = (e: Event) => {
		setVodMuted((e as CustomEvent).detail.isMuted)
	}
	const setVideoIdxValue = useVideoMetaFixed()
	const handlerVideoReadyToPlay = (e: Event) => {
		const { isReady } = (e as CustomEvent).detail

		if (isReady) {
			setVideoIdxValue(videoIdx)
		}
	}

	const handlerVodController = useCallback(() => {
		const isPaused = vodPlayer?.container
			.querySelector('.jtbcplayer-container')
			?.classList.value.split(' ')
			.includes('paused')
		if (isPaused) {
			setVodPlaying(false)
		} else {
			setVodPlaying(true)
		}

		nativeEvent({
			key: 'requestDeviceIsMobile',
			value: null,
		})
	}, [nativeEvent, vodPlayer])
	useEffect(() => {
		nativeEvent({
			key: 'requestDeviceIsMobile',
			value: null,
		})
	}, [nativeEvent])
	useEffect(() => {
		if (document.querySelector(`#${scriptId}`)) {
			handleDispatch()
		} else {
			const script = document.createElement('script')
			script.id = scriptId
			script.src = `${process.env.NEXT_PUBLIC_VIDEO_PLAYER_SCRIPT_URL}?today=${today}`
			script.type = 'module'
			script.defer = true
			script.onload = handleDispatch

			document.head.appendChild(script)
		}
	}, [isMounted, global?.JTBCVideoPlayer])
	useEffect(() => {
		vodPlayer?.container?.addEventListener('loadedmetadata', handleLoadedVideo)
		vodPlayer?.container?.addEventListener('ended', handleFinished)
		vodPlayer?.container?.addEventListener('changevideomute', handleChangeVideoMute)
		vodPlayer?.container?.addEventListener('changefullscreen', handleChangeFullscreen)
		vodPlayer?.container?.addEventListener('changeresolution', handleChangeResolution)
		vodPlayer?.container?.addEventListener('timeupdate', handleTimeUpdate)
		vodPlayer?.container?.addEventListener('videoreadytoplay', handlerVideoReadyToPlay)
		vodPlayer?.container?.addEventListener('click', handlerVodController)
		window.addEventListener('scroll', handlePIP)
		window.addEventListener('loadedPlayer', handleLoad)
		window.document.addEventListener('visibilitychange', handleVisibilityChange)

		return () => {
			window.document.removeEventListener('visibilitychange', handleVisibilityChange)
			window.removeEventListener('scroll', handlePIP)
			window.removeEventListener('loadedPlayer', handleLoad)
			vodPlayer?.container?.removeEventListener('changefullscreen', handleChangeFullscreen)
			vodPlayer?.container?.removeEventListener('changevideomute', handleChangeVideoMute)
			vodPlayer?.container?.removeEventListener('ended', handleFinished)
			vodPlayer?.container?.removeEventListener('loadedmetadata', handleLoadedVideo)
			vodPlayer?.container?.removeEventListener('changeresolution', handleChangeResolution)
			vodPlayer?.container?.removeEventListener('timeupdate', handleTimeUpdate)
			vodPlayer?.container?.removeEventListener('click', handlerVodController)
		}
	}, [
		vodPlayer,
		appInfo,
		handlePIP,
		handleVisibilityChange,
		handleChangeFullscreen,
		handleTimeUpdate,
	])
	useEffect(() => {
		const savedTimeData = localStorage.getItem(`video-${videoIdx}-currentTime`)
		const shouldAutoPlay = !savedTimeData || isArticlePage

		if (initAutoPlay || mobileDisabled) return
		if (vodPlayer && vodCanPlay && isAutoPlay && shouldAutoPlay) {
			if (vodElement && vodElement.paused) {
				setTimeout(() => {
					vodPlayer.play()
					setVodPlaying(true)
					setInitAutoPlay(true)
				}, 100)
			}
		}
	}, [
		initAutoPlay,
		isAutoPlay,
		mobileDisabled,
		vodCanPlay,
		vodElement,
		vodPlayer,
		videoIdx,
		isArticlePage,
	])
	useEffect(() => {
		if (isCommentDraw) {
			vodPlayer?.container.classList.add('open-drawer')
		} else {
			vodPlayer?.container.classList.remove('open-drawer')
		}
	}, [isCommentDraw])
	/** 미디어 연결 해제 */
	useEffect(() => {
		return () => {
			if (vodPlayer?.ref.current) {
				const currentTimeData = localStorage.getItem(`video-${videoIdx}-currentTime`)
				if (currentTimeData) {
					const existingData = JSON.parse(currentTimeData)
					const timeData = {
						...existingData,
						expiry: Date.now() + 60 * 60 * 1000,
					}
					localStorage.setItem(`video-${videoIdx}-currentTime`, JSON.stringify(timeData))
				}
			}
		}
	}, [vodPlayer, videoIdx, videoCurrentTime])

	useEffect(() => {
		return () => {
			if (vodPlayer?.ref.current) {
				vodPlayer?.onDestroyMedia?.()
			}
		}
	}, [vodPlayer])

	useEffect(() => {
		if (isVodPlaying && isTTSPlaying) {
			vodPlayer?.pause()
		}
	}, [isVodPlaying, isTTSPlaying, vodPlayer])

	const handleAppWithTTSButton = useRecoilCallback(
		({ snapshot }) =>
			async () => {
				const ttsOverlay = document.querySelector('.tts-video-overlay')
				if (ttsOverlay) {
					ttsOverlay.remove()
				}

				const currentTTSState = await snapshot.getPromise(TTSState)

				if (currentTTSState.isTTSPlaying) {
					notify('본문 듣기를 종료 하였습니다.')
				}

				setIsTTSPlaying(false)
				vodPlayer?.play()
			},
		[vodPlayer, setIsTTSPlaying, notify],
	)

	useEffect(() => {
		const playerContainer = document.getElementById('jtbc-video-player')

		if (playerContainer && isApp && isArticlePage && isTTSPlaying) {
			const existingOverlay = playerContainer.querySelector('.tts-video-overlay')
			if (!existingOverlay) {
				const buttonOverlay = document.createElement('button')
				buttonOverlay.className = 'tts-video-overlay'
				buttonOverlay.style.position = 'absolute'
				buttonOverlay.style.top = '50%'
				buttonOverlay.style.left = '50%'
				buttonOverlay.style.transform = 'translate(-50%, -50%)'
				buttonOverlay.style.width = '30%'
				buttonOverlay.style.height = '30%'
				buttonOverlay.style.zIndex = '12'
				buttonOverlay.style.display = 'flex'
				buttonOverlay.style.alignItems = 'center'
				buttonOverlay.style.justifyContent = 'center'
				buttonOverlay.onclick = handleAppWithTTSButton

				const root = createRoot(buttonOverlay)
				root.render(<IconArticleVideoPlay className="icon-lg" />)

				playerContainer.appendChild(buttonOverlay)
			}
		}
	}, [isApp, isArticlePage, isTTSPlaying, setIsTTSPlaying, handleAppWithTTSButton, notify])

	return (
		<>
			<Script
				stylesheets={[
					`${process.env.NEXT_PUBLIC_VIDEO_PLAYER_STYLESHEET_URL}?today=${today}`,
					'/styles/jtbc-video-player.css',
				]}
			/>
			{/* 영상 카운트 수집 */}
			<Script id="play-on-callback">
				{`window.playOnCallback =  function ($player, vodId) {
                    if($player == undefined){
                        return;
                    }
                    if ($player.dataset.loading) {
                        return;
                    }

                    var currentUrl = $player.dataset.currentUrl;
                    var apiHost = $player.dataset.apiHost;

                    //신규 플레이어 전환 중 vodId를 못불러오는 경우가 있어 예외 체크
                    if(!(vodId.toLowerCase() == 'onair' || vodId.toLowerCase() == 'on_air')){
                        var checkVodId = /^vo\\d{8}$/
                        if(!checkVodId.test(vodId.toLowerCase())){
                            vodId = $player.dataset.vod;
                        }
                    }

                    fetch(apiHost + '/vod/play-on-log?vod=' + vodId + '&url=' + currentUrl + '&deviceDiv=${
						appInfo && appInfo.DType === 'A'
							? 'AOS'
							: appInfo && appInfo.DType === 'I'
							? 'IOS'
							: isMobile
							? 'MO_WEB'
							: 'PC_WEB'
					}', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        credentials: 'include'
                    })
                    .then(response => response.json())
                    .finally(() => {
                        $player.dataset.loading = 'false';
                    });
                }`}
			</Script>
			<VideoContainer ratio={ratio} isVodPlaying={isVodPlaying} vodPlayer={vodPlayer}>
				{isMounted && (
					<Box
						ref={vodContainerRef}
						id={`vod-container-${videoIdx}`}
						className={['vod-container', ...(isApp ? ['is-app'] : [])].join(' ')}
						data-vod={videoIdx}
						data-current-url={window.location.href}
						data-api-host={process.env.NEXT_PUBLIC_JTBC_API}
						sx={{
							'& .jtbcplayer-container': {
								WebkitAspectRatio: ratio,
								height: '100%',
							},
							...(type === 'VOD_NEWS'
								? {
										'& .advertisement-container > .button-wrap': {
											'& > button:nth-last-of-type(2)': {
												display: 'none',
											},
										},
										'& .function-container > .right': {
											'& > button:nth-of-type(2):nth-last-of-type(4)': {
												display: 'none',
											},
											'& > button:nth-of-type(4):nth-last-of-type(2)': {
												display: 'none',
											},
										},
								  }
								: type === 'ON_AIR_NEWS'
								? {
										'& .function-container > .right': {
											'& > button:nth-of-type(3):nth-last-of-type(2)': {
												display: 'none',
											},
										},
								  }
								: {}),
						}}
						style={{
							/**
							 * FIXME: 플레이어 정식 릴리즈 후 사이트 스타일에 맞게 수정이 필요할 수 있음
							 */
							position: 'relative',
							width: '100%',
						}}
					/>
				)}
			</VideoContainer>
		</>
	)
}
